import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { X, AlertTriangle, Ban, UserCheck, UserX, Clock } from 'lucide-react';

const UserStatusModal = ({ user, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    status: user.status || 'active',
    reason: '',
    ban_reason: user.ban_reason || '',
    suspension_until: '',
    admin_notes: user.admin_notes || ''
  });
  
  const [isLoading, setIsLoading] = useState(false);

  const statusOptions = [
    { value: 'active', label: 'Active', icon: UserCheck, color: 'text-green-600' },
    { value: 'inactive', label: 'Inactive', icon: UserX, color: 'text-gray-600' },
    { value: 'suspended', label: 'Suspended', icon: Clock, color: 'text-yellow-600' },
    { value: 'banned', label: 'Banned', icon: Ban, color: 'text-red-600' }
  ];

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const processedData = {
        status: formData.status,
        reason: formData.reason || null
      };

      if (formData.status === 'suspended' && formData.suspension_until) {
        processedData.suspension_until = new Date(formData.suspension_until).toISOString();
      }

      if (formData.status === 'banned' && formData.ban_reason) {
        processedData.ban_reason = formData.ban_reason;
      }

      await onSave(processedData);
      onClose();
    } catch (error) {
      console.error('Error updating user status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusDescription = (status) => {
    switch (status) {
      case 'active':
        return 'User can fully access and use the application';
      case 'inactive':
        return 'User account is deactivated but not banned';
      case 'suspended':
        return 'User access is temporarily restricted';
      case 'banned':
        return 'User is permanently banned from the platform';
      default:
        return '';
    }
  };

  const selectedStatus = statusOptions.find(opt => opt.value === formData.status);
  const StatusIcon = selectedStatus?.icon || UserCheck;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-lg bg-white rounded-lg shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <AlertTriangle className="w-6 h-6 text-orange-500 mr-2" />
            <div>
              <h2 className="text-xl font-bold text-gray-900">Manage User Status</h2>
              <p className="text-sm text-gray-600">Update account status and restrictions</p>
            </div>
          </div>
          <Button
            onClick={onClose}
            variant="ghost"
            size="sm"
            className="rounded-full"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* User Info */}
        <div className="p-4 bg-gray-50 border-b">
          <div className="flex items-center space-x-4">
            <img
              src={user.images?.[0] || '/default-avatar.png'}
              alt={user.name}
              className="w-12 h-12 rounded-full object-cover"
            />
            <div>
              <h3 className="font-semibold text-gray-900">{user.name}</h3>
              <p className="text-sm text-gray-600">{user.email}</p>
              <p className="text-xs text-gray-500">
                Current Status: <span className="font-medium capitalize">{user.status || 'active'}</span>
              </p>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-6">
            {/* Status Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Account Status
              </label>
              <div className="space-y-3">
                {statusOptions.map((option) => {
                  const OptionIcon = option.icon;
                  return (
                    <label key={option.value} className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                      <input
                        type="radio"
                        name="status"
                        value={option.value}
                        checked={formData.status === option.value}
                        onChange={handleChange}
                        className="mr-3"
                      />
                      <OptionIcon className={`w-5 h-5 ${option.color} mr-3`} />
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{option.label}</div>
                        <div className="text-sm text-gray-500">{getStatusDescription(option.value)}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Conditional Fields */}
            {formData.status === 'suspended' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Suspension End Date
                </label>
                <Input
                  type="datetime-local"
                  name="suspension_until"
                  value={formData.suspension_until}
                  onChange={handleChange}
                  min={new Date().toISOString().slice(0, 16)}
                />
                <p className="text-xs text-gray-500 mt-1">Leave empty for indefinite suspension</p>
              </div>
            )}

            {formData.status === 'banned' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ban Reason
                </label>
                <textarea
                  name="ban_reason"
                  value={formData.ban_reason}
                  onChange={handleChange}
                  placeholder="Explain the reason for banning this user"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-red-500"
                  rows={3}
                  required={formData.status === 'banned'}
                />
              </div>
            )}

            {/* Reason for Change */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for Status Change
              </label>
              <textarea
                name="reason"
                value={formData.reason}
                onChange={handleChange}
                placeholder="Briefly explain why you're changing this user's status"
                className="w-full px-3 py-2 border border-gray-300 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
              />
            </div>

            {/* Warning */}
            {(formData.status === 'banned' || formData.status === 'suspended') && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start">
                  <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
                  <div className="text-sm text-red-800">
                    <p className="font-medium mb-1">Warning</p>
                    <p>
                      {formData.status === 'banned' 
                        ? 'Banning this user will permanently restrict their access to the platform.'
                        : 'Suspending this user will temporarily restrict their access to the platform.'
                      }
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 mt-8 pt-6 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              className={`${
                formData.status === 'banned' || formData.status === 'suspended'
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Updating...
                </>
              ) : (
                <>
                  <StatusIcon className="w-4 h-4 mr-2" />
                  Update Status
                </>
              )}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default UserStatusModal;